import { useEffect, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import './SplashScreen.css';

// 3D Model Component
function PaperBoatModel({ onLoaded }) {
  const meshRef = useRef();
  const { scene } = useGLTF('/paper_boat.glb', true, true);
  const [scale, setScale] = useState([45, 45, 45]);
  const [position, setPosition] = useState([0.5, -0.3, 0]);

  useEffect(() => {
    if (scene) {
      // Notificar que el modelo está cargado
      onLoaded();
    }
  }, [scene, onLoaded]);

  // Adjust scale based on screen size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setScale([48, 48, 48]);
        setPosition([0.3, -0.2, 0]);
      } else if (width <= 768) {
        setScale([46, 46, 46]);
        setPosition([0.4, -0.25, 0]);
      } else {
        setScale([45, 45, 45]);
        setPosition([0.5, -0.3, 0]);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-rotate animation (slower on mobile)
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const rotationSpeed = isMobile ? 0.003 : 0.01;

    const animate = () => {
      if (meshRef.current) {
        meshRef.current.rotation.y += rotationSpeed;
      }
      requestAnimationFrame(animate);
    };
    animate();
  }, []);

  return (
    <primitive
      ref={meshRef}
      object={scene}
      scale={scale}
      position={position}
    />
  );
}

const SplashScreen = ({ onComplete }) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [showSplash, setShowSplash] = useState(false);

  // Mostrar splash screen solo cuando el modelo esté cargado
  useEffect(() => {
    if (modelLoaded) {
      // Pequeño delay para animación de entrada suave
      setTimeout(() => {
        setShowSplash(true);
      }, 100);
    }
  }, [modelLoaded]);

  // Iniciar barra de progreso solo cuando el modelo esté cargado
  useEffect(() => {
    if (!modelLoaded) return;

    const interval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          // Start fade out animation after a pause
          setTimeout(() => {
            setFadeOut(true);
            // Complete after fade animation
            setTimeout(() => {
              onComplete();
            }, 1000);
          }, 300); // Wait 0.3 seconds at 100%
          return 100;
        }
        return prev + 2.5; // 2.5% increment
      });
    }, 25); // 25ms * 40 = 1 second total

    return () => clearInterval(interval);
  }, [modelLoaded, onComplete]);

  const handleModelLoaded = () => {
    setModelLoaded(true);
  };

  return (
    <div className={`splash-screen ${fadeOut ? 'fade-out' : ''}`}>
      <div className="splash-content" style={{
        opacity: modelLoaded ? 1 : 0,
        transform: modelLoaded ? 'scale(1)' : 'scale(0.95)'
      }}>
        {/* 3D Model Canvas */}
        <div className="model-container">
          <Canvas
            camera={{ position: [0, 1, 12], fov: 50 }}
            style={{ background: 'transparent' }}
          >
            <ambientLight intensity={1.2} />
            <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={2} />
            <pointLight position={[-10, -10, -10]} intensity={1.5} />
            <directionalLight position={[0, 10, 5]} intensity={1.5} />
            <PaperBoatModel onLoaded={handleModelLoaded} />
          </Canvas>
        </div>

        {/* Logo Text */}
        <div className="splash-logo">
          <h1 className="splash-title">PAPER BOAT</h1>
        </div>

        {/* Loading Bar */}
        <div className="loading-container">
          <div className="loading-bar">
            <div
              className="loading-progress"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
          <p className="loading-text">Cargando... {loadingProgress}%</p>
        </div>

        {/* Animated Waves */}
        <div className="splash-waves">
          <div className="splash-wave splash-wave1"></div>
          <div className="splash-wave splash-wave2"></div>
          <div className="splash-wave splash-wave3"></div>
        </div>
      </div>
    </div>
  );
};

// Preload the GLB model
useGLTF.preload('/paper_boat.glb');

export default SplashScreen;